import {
    createRootRouteWithContext,
    ErrorComponentProps as ErrorProps,
    notFound,
    Outlet,
    useLocation,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { getStrictCurrentEnterpriseId, updateCurrentSession } from '@mews-commander/core';
import { GUID_FORMAT_REGEX, RouterContext, useDocumentTitle } from '@mews-commander/modules';
import { BackendComponents, ErrorHandler } from '@mews-commander/ui';
import { MewsTeam, RouteType } from '@mews/enums';
import { LocalizedText, useLoggerService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { EnterpriseIdFallbackComponent } from '../fallback';

export const Route = createRootRouteWithContext<RouterContext>()({
    beforeLoad: ({ params }) => {
        // update the current enterprise ID if it's provided in the URL
        if ('currentEnterpriseId' in params && typeof params.currentEnterpriseId === 'string') {
            const { currentEnterpriseId } = params;
            if (!GUID_FORMAT_REGEX.test(currentEnterpriseId)) {
                throw notFound();
            }
            const previousEnterpriseId = getStrictCurrentEnterpriseId();
            if (currentEnterpriseId !== previousEnterpriseId) {
                updateCurrentSession({ currentEnterpriseId });
                /**
                 * Without the timeout the reload would hit before the new URL is reflected in the browser,
                 * meaning it would reload the previous page and not actually change the enterprise.
                 */
                window.setTimeout(() => {
                    window.location.reload();
                });
            }
        }
    },
    component: () => <OutletComponent />,
    notFoundComponent: () => <EnterpriseIdFallbackComponent notFoundComponent={<NotFoundComponent />} />,
    errorComponent: ({ error, reset }) => <ErrorComponent error={error} reset={reset} />,
    staticData: {
        owner: MewsTeam.Productivity,
        type: RouteType.Frontend,
        excludeBreadcrumb: true,
    },
});

const OutletComponent = () => {
    useDocumentTitle();

    return (
        <>
            <BackendComponents />
            <Outlet />
        </>
    );
};

const NotFoundComponent = () => {
    const loggerService = useLoggerService();
    const location = useLocation();

    const documentationLink = 'https://mews.atlassian.net/wiki/spaces/FE/pages/311099505/Routing+concepts';

    useEffect(() => {
        loggerService.log('fe-routing-error', `Route not found in the TanStack Router, see ${documentationLink}`, {
            pathname: location.pathname,
            search: location.search,
            fragment: location.hash,
        });
    }, [location.hash, location.pathname, location.search, loggerService]);

    return (
        <ErrorHandler
            errorMessage={<LocalizedText textKey={TextKey.PageNotFoundErrorMessage} />}
            errorTitle={<LocalizedText textKey={TextKey.PageNotFoundTitle} />}
        />
    );
};

type ErrorComponentProps = Pick<ErrorProps, 'error' | 'reset'>;

const ErrorComponent = ({ error, reset }: ErrorComponentProps) => {
    const loggerService = useLoggerService();
    const location = useLocation();

    useEffect(() => {
        loggerService.log('fe-routing-error', `Route error in the TanStack Router, ${error.message}`, {
            errorMessage: error.message,
            errorStack: error.stack,
            errorName: error.name,
            pathname: location.pathname,
            search: location.search,
            fragment: location.hash,
        });
    }, [error.message, error.name, error.stack, location.hash, location.pathname, location.search, loggerService]);

    return (
        <ErrorHandler
            errorMessage={<LocalizedText textKey={TextKey.GenericErrorMessage} />}
            errorTitle={<LocalizedText textKey={TextKey.GenericErrorTitle} />}
            onDismiss={reset}
        />
    );
};
